import { useEffect, useState } from "react";

export const VIEWER_LANGUAGE_EVENT = "viewerLanguageChanged";

const currentTlb = () => window?.objInit?.tlb;

const payloadFor = (tlb = currentTlb()) =>
  tlb?.personal_insights_copy?.locales ? tlb.personal_insights_copy : null;

const storageKey = (tlb) => `kz_lang_${tlb?.token || "default"}`;

const browserLocale = (payload) => {
  const codes = Object.keys(payload.locales);
  const preferred = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]).filter(Boolean);
  for (const tag of preferred) {
    const exact = codes.find((code) => code.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;
    const base = tag.split("-")[0].toLowerCase();
    const zh = base === "zh" ? codes.find((code) => code === "zh-CN") : null;
    if (zh) return zh;
    const match = codes.find((code) => code.toLowerCase() === base);
    if (match) return match;
  }
  return null;
};

export const isViewerLanguageEnabled = (tlb = currentTlb()) => !!payloadFor(tlb);

export const getViewerLocale = (tlb = currentTlb()) => {
  const payload = payloadFor(tlb);
  if (!payload) return null;
  let stored = null;
  try {
    stored = sessionStorage.getItem(storageKey(tlb));
  } catch (error) {
    stored = window.__kzViewerLocale || null;
  }
  if (stored && payload.locales[stored]) return stored;
  const browser = browserLocale(payload);
  if (browser) return browser;
  return payload.locales[payload.default_locale] ? payload.default_locale : "en";
};

export const setViewerLocale = (locale, tlb = currentTlb()) => {
  const payload = payloadFor(tlb);
  if (!payload || !payload.locales[locale]) return;
  try {
    sessionStorage.setItem(storageKey(tlb), locale);
  } catch (error) {
    window.__kzViewerLocale = locale;
  }
  window.dispatchEvent(new CustomEvent(VIEWER_LANGUAGE_EVENT, { detail: { locale } }));
};

export const getViewerCopy = (tlb = currentTlb()) => {
  const payload = payloadFor(tlb);
  if (!payload) return null;
  return payload.locales[getViewerLocale(tlb)] || null;
};

export const useViewerCopy = (tlb = currentTlb()) => {
  const [locale, setLocale] = useState(() => getViewerLocale(tlb));

  useEffect(() => {
    const onChange = () => setLocale(getViewerLocale(tlb));
    window.addEventListener(VIEWER_LANGUAGE_EVENT, onChange);
    return () => window.removeEventListener(VIEWER_LANGUAGE_EVENT, onChange);
  }, [tlb]);

  const payload = payloadFor(tlb);
  const copy = payload && locale ? payload.locales[locale] : null;
  return {
    enabled: !!payload,
    variant: payload?.variant || "reference",
    values: copy?.values || {},
    locale,
    dir: copy?.dir || "ltr",
    copy,
    languages: payload?.languages || [],
    setLocale: (next) => setViewerLocale(next, tlb),
  };
};
